import { init, InstantAPIError, type ValidQuery } from '@instantdb/admin';
import { db } from './instant';
import schema, { type AppSchema } from '../../instant.schema';

const REQUEST_TIMEOUT_MS = 15_000;

export class ServerWriteError extends Error {
  constructor(
    message: string,
    public readonly outcome: 'not-sent' | 'rejected' | 'unknown',
  ) {
    super(message);
    this.name = 'ServerWriteError';
  }
}

async function userServerDb() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new ServerWriteError('You are offline. Reconnect before saving.', 'not-sent');
  }
  let user;
  try { user = await db.getAuth(); }
  catch { throw new ServerWriteError('Unable to verify your session. Sign in again.', 'not-sent'); }
  if (!user?.refresh_token) {
    throw new ServerWriteError('Sign in again before saving.', 'not-sent');
  }
  const appId = process.env.EXPO_PUBLIC_INSTANT_APP_ID;
  if (!appId) {
    throw new ServerWriteError('The database is not configured.', 'not-sent');
  }
  // Officially supported client mode: only the user's token is supplied.
  // No admin credentials, React optimistic cache, or automatic offline queue.
  // https://www.instantdb.com/docs/backend#running-the-admin-sdk-in-client-environments
  return init({ appId, schema }).asUser({ token: user.refresh_token });
}

/** A resolved call means Instant returned a committed transaction ID. */
export async function transactServer(
  changes: Parameters<typeof db.transact>[0],
): Promise<void> {
  const userDb = await userServerDb();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      userDb.transact(changes),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new ServerWriteError(
          'Save confirmation was lost. Check the server before retrying this change.',
          'unknown',
        )), REQUEST_TIMEOUT_MS);
      }),
    ]);
    if (!Number.isSafeInteger(result?.['tx-id']) || result['tx-id'] <= 0) {
      throw new ServerWriteError('The server did not confirm this save. Check before retrying.', 'unknown');
    }
  } catch (error) {
    if (error instanceof ServerWriteError) throw error;
    if (error instanceof InstantAPIError && error.status >= 400 && error.status < 500 && error.status !== 408) {
      const message = error.status === 401 ? 'Your session expired. Sign in again.'
        : error.status === 403 ? 'You do not have permission to save this change.'
        : error.status === 429 ? 'Too many requests. Wait a moment before saving again.'
        : 'The server rejected this change. Review the values and try again.';
      throw new ServerWriteError(message, 'rejected');
    }
    // A disconnect/5xx/timeout can occur after commit. Never claim failure or
    // automatically replay a create with a fresh record ID in this case.
    throw new ServerWriteError('Save confirmation was lost. Check the server before retrying this change.', 'unknown');
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

/** Authoritative readback; db.queryOnce() can include queued optimistic writes. */
export async function queryServer<Q extends ValidQuery<Q, AppSchema>>(query: Q) {
  const userDb = await userServerDb();
  try {
    return await userDb.query(query, {
      fetchOpts: { cache: 'no-store', signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) },
    });
  } catch {
    throw new Error('Unable to verify saved data. Check your connection and reload.');
  }
}

/** Recover a lost create response using its stable ID and an exact server read. */
export async function transactCreateServer(
  changes: Parameters<typeof transactServer>[0],
  existsExactly: () => Promise<boolean>,
): Promise<void> {
  try {
    await transactServer(changes);
  } catch (error) {
    try {
      if (await existsExactly()) return;
    } catch {
      // Keep the original outcome if authoritative readback is unavailable.
    }
    throw error;
  }
}

/** Compare requested fields, including canonical date representations. */
export function matchesSavedFields(saved: Record<string, unknown>, expected: Record<string, unknown>): boolean {
  return Object.entries(expected).every(([field, value]) => {
    if (value instanceof Date) {
      const stored = saved[field];
      return (typeof stored === 'string' || typeof stored === 'number' || stored instanceof Date)
        && new Date(stored).getTime() === value.getTime();
    }
    return JSON.stringify(saved[field]) === JSON.stringify(value);
  });
}
